import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  LifeBuoy, Send, ArrowLeft, ImagePlus, X, Bug, Lightbulb,
  HelpCircle, MessageSquare, Loader2, Inbox, Plus, BookOpen,
} from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";

// ---- option tables ----------------------------------------------------------
const CATEGORIES = [
  { id: "bug", label: "Błąd w aplikacji", icon: Bug },
  { id: "feature", label: "Pomysł / nowa funkcja", icon: Lightbulb },
  { id: "support", label: "Pytanie / pomoc", icon: HelpCircle },
  { id: "other", label: "Inne", icon: MessageSquare },
];
const PRIORITIES = [
  { id: "low", label: "Niski" },
  { id: "normal", label: "Normalny" },
  { id: "high", label: "Wysoki" },
  { id: "urgent", label: "Pilny" },
];
const STATUSES = [
  { id: "open", label: "Otwarte", color: "#3b82f6" },
  { id: "in_progress", label: "W toku", color: "#f59e0b" },
  { id: "resolved", label: "Rozwiązane", color: "#22c55e" },
  { id: "closed", label: "Zamknięte", color: "#6b7280" },
];

const catLabel = (id) => (CATEGORIES.find((c) => c.id === id) || {}).label || id;
const prioLabel = (id) => (PRIORITIES.find((p) => p.id === id) || {}).label || id;
const statusMeta = (id) => STATUSES.find((s) => s.id === id) || { label: id, color: "#6b7280" };

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const EMPTY_FORM = { category: "support", priority: "normal", subject: "", description: "" };

// ---- in-app guide content (condensed from docs/manuals/*) --------------------
const GUIDE_CLIENT = [
  {
    title: "Pierwsze kroki",
    points: [
      "DuoFit działa też samodzielnie — bez trenera korzystasz z treningów, biegania, diety AI, pomiarów i nawyków.",
      "Twoje dane są prywatne. Dopóki nie połączysz się z trenerem, nikt poza Tobą ich nie widzi.",
      "Ekran „Dzisiaj” to szybki przegląd dnia: treningi, nawyki, nawodnienie, posiłki.",
    ],
  },
  {
    title: "Treningi",
    points: [
      "Biblioteka ćwiczeń z animacjami GIF — wyszukaj lub dodaj własne ćwiczenie.",
      "Twórz plany (serie, powtórzenia, czas, Tabata) i odtwarzaj je dzień po dniu.",
      "Po treningu kliknij „Zapisz trening” — dostajesz XP, historia w zakładce Treningi.",
    ],
  },
  {
    title: "Bieganie z AI Coachem",
    points: [
      "Bieganie → Start, opcjonalnie wybierz plan interwałowy, zezwól na GPS.",
      "AI Coach mówi po polsku: reguły na bieżąco + wiadomość co ~2 min.",
      "Po zakończeniu: analiza AI — splity kilometrowe, wykres tempa, podsumowanie.",
    ],
  },
  {
    title: "Pomiary, nawyki, odżywianie",
    points: [
      "Pomiary: waga i obwody z wykresami, zdjęcia progresu.",
      "Nawyki: codzienne odhaczanie buduje streak i daje XP; nawodnienie z przypomnieniami.",
      "Odżywianie: logowanie posiłków z wyszukiwarką produktów (makra liczą się same).",
    ],
  },
  {
    title: "Dieta AI",
    points: [
      "Ustaw kaloryczność, typ diety i liczbę dni — generator ułoży posiłki.",
      "Dostajesz listę zakupów pogrupowaną działami sklepu (nie alfabetycznie).",
      "Opcja „przepisy Thermomix” z krokami TM. Wygenerowaną dietę możesz zapisać.",
    ],
  },
  {
    title: "Połączenie z trenerem",
    points: [
      "Poproś trenera o Kod Trenera → Ustawienia → Połącz z trenerem → wpisz kod.",
      "Połączenie NIE kasuje Twoich danych — historia treningów, pomiarów i diet zostaje.",
      "Możesz odłączyć się od trenera w Ustawieniach w każdej chwili.",
    ],
  },
];

const GUIDE_TRAINER = [
  {
    title: "Klienci i kod trenera",
    points: [
      "Twój unikalny Kod Trenera (Ustawienia / pulpit) — klient wpisuje go, by się połączyć.",
      "Klienci: lista podopiecznych z podsumowaniem; wejście w klienta = pełne szczegóły.",
      "Zespół (Klienci → Zespół): struktura trenerska; w firmie widzisz całe drzewo firmy.",
    ],
  },
  {
    title: "Plany i dieta",
    points: [
      "Plany treningowe i biegowe — twórz i przypisuj klientom; biegowe działają z AI Coachem.",
      "Dieta AI: generuj i przypisuj diety klientom (lista zakupów działami, Thermomix).",
      "Przepisy: edytor z ręczną korektą makro (kcal/B/T/W).",
    ],
  },
  {
    title: "Grafik, sesje, zastępstwa",
    points: [
      "Nowa sesja: klient, data, godzina, czas, lokalizacja — system sprawdza konflikty czasowe.",
      "Edycja sesji = przesunięcie z ponownym sprawdzeniem kolizji.",
      "„Zastąp” na nadchodzącej sesji → przepięcie na innego dostępnego trenera firmy.",
    ],
  },
  {
    title: "Dostępność i urlopy",
    points: [
      "Ustaw cykliczny grafik dostępności — firma widzi Twoje wolne okna (Firma → Grafik → Dostępność).",
      "Wolne okno = grafik − urlopy/wyjątki − zarezerwowane sesje.",
      "Wniosek urlopowy → status „oczekujący” → zatwierdza właściciel/menedżer; po zatwierdzeniu blokuje dostępność i kolidujące sesje.",
    ],
  },
  {
    title: "Komunikacja",
    points: [
      "Formularze: kreator → przypisz klientowi → odpowiedzi w aplikacji.",
      "Alerty (red flags): automatyczne sygnały o klientach wymagających uwagi.",
      "Czat w czasie rzeczywistym z klientem.",
    ],
  },
  {
    title: "Dla właścicieli / adminów firmy",
    points: [
      "Panel „Firma”: współwłaściciele, członkowie, struktura (przełożony + lokalizacje), lokalizacje.",
      "Onboarding/offboarding: dezaktywacja trenera usuwa go z zespołu i grafiku (dane zostają, można reaktywować).",
      "Grafik firmy: Sesje (wg lokalizacji) lub Dostępność (kto i kiedy wolny — do zastępstw/nowych klientów).",
      "Konfiguracja per-firma (klucze, ceny) edytowalna w UI; zgłoszenia przypisane do firmy.",
    ],
  },
];

const HelpButton = () => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("new"); // new | list | detail
  const [isAdmin, setIsAdmin] = useState(false);
  const [badge, setBadge] = useState(0);

  // form
  const [form, setForm] = useState(EMPTY_FORM);
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // list
  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [scope, setScope] = useState("mine"); // mine | all (admin only)
  const [statusFilter, setStatusFilter] = useState("all");

  // detail
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  // ---- badge / admin flag ---------------------------------------------------
  const refreshBadge = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api("get", "/tickets/stats", null, token);
      // Two separate powers since the flag was split: the DuoFit support team and
      // a business-organization admin handling their own firm's tickets. Either one
      // gets the queue.
      setIsAdmin(!!res.data.is_support_admin || !!res.data.is_firm_ticket_admin);
      setBadge(res.data.needs_attention || 0);
    } catch {
      /* silent — help button must never break the dashboard */
    }
  }, [token]);

  useEffect(() => { refreshBadge(); }, [refreshBadge]);

  // ---- list -----------------------------------------------------------------
  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = [];
      if (isAdmin && scope === "all") params.push("scope=all");
      else params.push("scope=mine");
      if (statusFilter !== "all") params.push(`status=${statusFilter}`);
      const res = await api("get", `/tickets?${params.join("&")}`, null, token);
      setTickets(res.data.tickets || []);
    } catch (err) {
      toast.error("Nie udało się pobrać zgłoszeń");
    } finally {
      setLoadingList(false);
    }
  }, [token, isAdmin, scope, statusFilter]);

  useEffect(() => {
    if (open && view === "list") loadList();
  }, [open, view, loadList]);

  // ---- screenshot -----------------------------------------------------------
  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Wybierz plik graficzny");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Downscale to keep the payload small (~max 1280px wide, JPEG 0.7).
        const maxW = 1280;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setScreenshot(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = () => toast.error("Nie udało się wczytać obrazu");
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // ---- submit new ticket ----------------------------------------------------
  const submitTicket = async () => {
    if (form.subject.trim().length < 3) { toast.error("Podaj krótki temat (min. 3 znaki)"); return; }
    if (form.description.trim().length < 5) { toast.error("Opisz problem dokładniej"); return; }
    setSubmitting(true);
    try {
      await api("post", "/tickets", {
        category: form.category,
        priority: form.priority,
        subject: form.subject.trim(),
        description: form.description.trim(),
        screenshot: screenshot || null,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      }, token);
      toast.success("Zgłoszenie wysłane — odpowiemy najszybciej jak to możliwe");
      setForm(EMPTY_FORM);
      setScreenshot(null);
      setView("list");
      refreshBadge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wysłać zgłoszenia");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- detail ---------------------------------------------------------------
  const openDetail = async (id) => {
    setView("detail");
    setDetail(null);
    setLoadingDetail(true);
    try {
      const res = await api("get", `/tickets/${id}`, null, token);
      setDetail(res.data);
    } catch (err) {
      toast.error("Nie udało się otworzyć zgłoszenia");
      setView("list");
    } finally {
      setLoadingDetail(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api("post", `/tickets/${detail.id}/reply`, { message: replyText.trim() }, token);
      setReplyText("");
      const res = await api("get", `/tickets/${detail.id}`, null, token);
      setDetail(res.data);
      refreshBadge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wysłać odpowiedzi");
    } finally {
      setBusy(false);
    }
  };

  const patchTicket = async (changes) => {
    setBusy(true);
    try {
      await api("patch", `/tickets/${detail.id}`, changes, token);
      const res = await api("get", `/tickets/${detail.id}`, null, token);
      setDetail(res.data);
      refreshBadge();
      toast.success("Zaktualizowano zgłoszenie");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zaktualizować");
    } finally {
      setBusy(false);
    }
  };

  if (!user || !token) return null;

  const bottomOffset = user.role === "client" ? 84 : 24;

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        aria-label="Pomoc i zgłoszenia"
        data-testid="help-button"
        onClick={() => { setOpen(true); setView("guide"); }}
        style={{
          position: "fixed", right: 20, bottom: bottomOffset, zIndex: 95,
          width: 52, height: 52, borderRadius: "50%", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff",
          boxShadow: "0 6px 20px rgba(99,102,241,0.45)",
        }}
      >
        <LifeBuoy size={24} />
        {badge > 0 && (
          <span style={{
            position: "absolute", top: -2, right: -2, minWidth: 20, height: 20,
            padding: "0 5px", borderRadius: 10, background: "#ef4444", color: "#fff",
            fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center",
            justifyContent: "center", border: "2px solid #0d0d0d",
          }}>{badge > 99 ? "99+" : badge}</span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <LifeBuoy size={20} />
              {view === "guide" && "Przewodnik po DuoFit"}
              {view === "new" && "Zgłoś problem lub pomysł"}
              {view === "list" && "Twoje zgłoszenia"}
              {view === "detail" && "Szczegóły zgłoszenia"}
            </DialogTitle>
            <DialogDescription>
              {view === "guide" && "Jak korzystać z aplikacji w Twojej roli."}
              {view === "new" && "Opisz problem — zespół DuoFit dostanie powiadomienie."}
              {view === "list" && (isAdmin ? "Kolejka wsparcia." : "Status Twoich zgłoszeń.")}
              {view === "detail" && "Konwersacja w sprawie zgłoszenia."}
            </DialogDescription>
          </DialogHeader>

          {/* Top nav between Guide / New / List */}
          {view !== "detail" && (
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant={view === "guide" ? "default" : "outline"} size="sm"
                style={{ flex: 1 }} onClick={() => setView("guide")}>
                <BookOpen size={14} /> Przewodnik
              </Button>
              <Button variant={view === "new" ? "default" : "outline"} size="sm"
                style={{ flex: 1 }} onClick={() => setView("new")}>
                <Plus size={14} /> Zgłoś
              </Button>
              <Button variant={view === "list" ? "default" : "outline"} size="sm"
                style={{ flex: 1 }} onClick={() => setView("list")}>
                <Inbox size={14} /> {isAdmin ? "Kolejka" : "Moje"}
              </Button>
            </div>
          )}

          <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
            {/* ===================== GUIDE ===================== */}
            {view === "guide" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(user.role === "trainer" ? GUIDE_TRAINER : GUIDE_CLIENT).map((sec, i) => (
                  <details key={sec.title} open={i === 0}
                    style={{
                      borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.03)", padding: "10px 12px",
                    }}>
                    <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 14, listStyle: "none" }}>
                      {sec.title}
                    </summary>
                    <ul style={{ margin: "8px 0 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                      {sec.points.map((p, j) => (
                        <li key={j} style={{ fontSize: 13, lineHeight: 1.45, color: "var(--text-secondary)" }}>{p}</li>
                      ))}
                    </ul>
                  </details>
                ))}
                <p style={{ fontSize: 12, color: "var(--text-secondary)", textAlign: "center", marginTop: 4 }}>
                  Nie znalazłeś odpowiedzi? Przejdź do <strong>Zgłoś</strong> i napisz do nas.
                </p>
              </div>
            )}

            {/* ===================== NEW ===================== */}
            {view === "new" && (
              <div className="dialog-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="field">
                  <Label>Kategoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="field">
                  <Label>Priorytet</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="field">
                  <Label>Temat</Label>
                  <Input
                    value={form.subject}
                    maxLength={200}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="np. Nie zapisuje się trening"
                    data-testid="ticket-subject"
                  />
                </div>
                <div className="field">
                  <Label>Opis</Label>
                  <Textarea
                    value={form.description}
                    maxLength={5000}
                    rows={5}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Co się stało? Jakie kroki to wywołują? Czego oczekiwałeś?"
                    data-testid="ticket-description"
                  />
                </div>
                <div className="field">
                  <Label>Zrzut ekranu (opcjonalnie)</Label>
                  {screenshot ? (
                    <div style={{ position: "relative", marginTop: 4 }}>
                      <img src={screenshot} alt="zrzut ekranu"
                        style={{ width: "100%", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)" }} />
                      <button type="button" onClick={() => setScreenshot(null)}
                        style={{
                          position: "absolute", top: 6, right: 6, width: 28, height: 28,
                          borderRadius: "50%", border: "none", cursor: "pointer",
                          background: "rgba(0,0,0,0.7)", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                      <ImagePlus size={14} /> Dodaj zrzut ekranu
                    </Button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*"
                    onChange={onPickFile} style={{ display: "none" }} />
                </div>
                <Button onClick={submitTicket} disabled={submitting} data-testid="ticket-submit">
                  {submitting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                  {submitting ? " Wysyłanie..." : " Wyślij zgłoszenie"}
                </Button>
              </div>
            )}

            {/* ===================== LIST ===================== */}
            {view === "list" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {isAdmin && (
                    <Select value={scope} onValueChange={setScope}>
                      <SelectTrigger style={{ flex: 1, minWidth: 130 }}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mine">Moje zgłoszenia</SelectItem>
                        <SelectItem value="all">Wszystkie</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger style={{ flex: 1, minWidth: 130 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Wszystkie statusy</SelectItem>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {loadingList ? (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <Loader2 className="spin" size={24} />
                  </div>
                ) : tickets.length === 0 ? (
                  <p style={{ textAlign: "center", color: "var(--text-secondary)", padding: 24 }}>
                    Brak zgłoszeń.
                  </p>
                ) : (
                  tickets.map((t) => {
                    const sm = statusMeta(t.status);
                    return (
                      <button key={t.id} type="button" onClick={() => openDetail(t.id)}
                        data-testid="ticket-row"
                        style={{
                          textAlign: "left", cursor: "pointer", padding: 12, borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)",
                          display: "flex", flexDirection: "column", gap: 6,
                        }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{t.subject}</span>
                          <Badge style={{ background: sm.color, color: "#fff", flexShrink: 0 }}>{sm.label}</Badge>
                        </div>
                        <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--text-secondary)", flexWrap: "wrap" }}>
                          <span>{catLabel(t.category)}</span>
                          <span>•</span>
                          <span>{prioLabel(t.priority)}</span>
                          {t.reply_count > 0 && (<><span>•</span><span>{t.reply_count} odp.</span></>)}
                          <span>•</span>
                          <span>{fmtDate(t.updated_at)}</span>
                        </div>
                        {isAdmin && scope === "all" && (
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            Od: {t.user_name} ({t.user_role})
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {/* ===================== DETAIL ===================== */}
            {view === "detail" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Button variant="ghost" size="sm" style={{ alignSelf: "flex-start" }}
                  onClick={() => { setView("list"); setDetail(null); }}>
                  <ArrowLeft size={14} /> Wróć do listy
                </Button>

                {loadingDetail || !detail ? (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <Loader2 className="spin" size={24} />
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{detail.subject}</h3>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Badge style={{ background: statusMeta(detail.status).color, color: "#fff" }}>
                          {statusMeta(detail.status).label}
                        </Badge>
                        <Badge variant="outline">{catLabel(detail.category)}</Badge>
                        <Badge variant="outline">Priorytet: {prioLabel(detail.priority)}</Badge>
                      </div>
                      <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6 }}>
                        {isAdmin ? `Od: ${detail.user_name} • ${detail.user_email} • ` : ""}
                        Zgłoszono {fmtDate(detail.created_at)}
                      </p>
                    </div>

                    {/* Admin controls */}
                    {isAdmin && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Select value={detail.status} onValueChange={(v) => patchTicket({ status: v })}>
                          <SelectTrigger style={{ flex: 1, minWidth: 130 }}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s.id} value={s.id}>Status: {s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={detail.priority} onValueChange={(v) => patchTicket({ priority: v })}>
                          <SelectTrigger style={{ flex: 1, minWidth: 130 }}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PRIORITIES.map((p) => (
                              <SelectItem key={p.id} value={p.id}>Priorytet: {p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Original description */}
                    <div style={{
                      padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)", fontSize: 14, whiteSpace: "pre-wrap",
                    }}>
                      {detail.description}
                    </div>
                    {detail.screenshot && (
                      <a href={detail.screenshot} target="_blank" rel="noreferrer">
                        <img src={detail.screenshot} alt="zrzut ekranu"
                          style={{ width: "100%", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)" }} />
                      </a>
                    )}
                    {isAdmin && detail.page_url && (
                      <p style={{ fontSize: 11, color: "var(--text-secondary)", wordBreak: "break-all" }}>
                        Strona: {detail.page_url}
                      </p>
                    )}

                    {/* Replies */}
                    {(detail.replies || []).map((r) => (
                      <div key={r.id} style={{
                        padding: 10, borderRadius: 10, fontSize: 14,
                        background: r.is_staff ? "rgba(99,102,241,0.12)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${r.is_staff ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.08)"}`,
                        alignSelf: r.is_staff ? "flex-end" : "flex-start", maxWidth: "92%",
                      }}>
                        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                          {r.is_staff ? "🛟 " : ""}{r.author_name}{r.is_staff ? " (wsparcie)" : ""} • {fmtDate(r.created_at)}
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{r.message}</div>
                      </div>
                    ))}

                    {/* Reply box */}
                    {detail.status === "closed" ? (
                      <p style={{ textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
                        Zgłoszenie zamknięte.
                      </p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <Textarea
                          value={replyText}
                          rows={3}
                          maxLength={5000}
                          placeholder="Napisz odpowiedź..."
                          onChange={(e) => setReplyText(e.target.value)}
                          data-testid="ticket-reply-text"
                        />
                        <Button onClick={sendReply} disabled={busy || !replyText.trim()}
                          data-testid="ticket-reply-send">
                          {busy ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                          {busy ? " Wysyłanie..." : " Wyślij odpowiedź"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpButton;
